import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Layout,
  PageHeader,
  Tabs,
  Button,
  Spin,
  message,
  Modal,
  Breadcrumb,
  notification
} from 'antd';
import isEmpty from 'lodash/isEmpty';
import style from './index.scss';
import Typologies from './TypologiesTab';
import RiskIndicators from './RiskIndicatorsTab';
import DataSet from './DatasetTab';
import { CONFIRM_PROPS, SCENARIO_FAILED, RETURN_TO_HOME } from './constants';
import {
  dateTimeFormat,
  TM_FRAUD_SOURCE_IDS
} from '../../../util/GlobalConstant';
import { convertLocalTimeToEpoch } from '../../../util/timezone';
import DynamicProperties from '../../common/DynamicProperties';
import { hasAttributeValueByAttributeKey } from '../../../util';
import { getSelectedResources } from '../../../util/resourceManager';
import { getStrategyType } from '../../../util/localStorageUtils';

const { TabPane } = Tabs;
const { TM_UI_RI_DYNPROPS, TM_UI_TYPOLOGY_DYNPROPS } = getSelectedResources();

const ConfigureScenario = ({
  createRunInstanceLiveSets,
  trmDashboard,
  scenario,
  scenarioAPIActions = {},
  configureRunInstanceAPIActions = {},
  match = { params: {} },
  isLoading,
  error,
  history,
  authentication,
  typologySegment
}) => {
  const { fetchSingleScenario, triggerScenariosInit, triggerScenarios } =
    scenarioAPIActions;

  const typologiesData =
    trmDashboard.scenarioTypologies?.data.segmentCombinations;
  const [runDataSet, setRunDataSet] = useState(null);
  const [tabActiveKey, setTabActiveKey] = useState('1');
  const [isValueEdited, setIsValueEdited] = useState(false);
  const [resetRI, setResetRI] = useState(false);
  const [preventionAccess, setPreventionAccess] = useState(false);
  const returnToRunInstance = () => {
    history.push(RETURN_TO_HOME);
  };

  const scenarioID = match.params.id;

  const checkTabAccess = (uiResource) => {
    if (!authentication?.data) return false;

    const strategyType = getStrategyType();

    // For Keycloak, check if the resource operations include the uiResource
    if (strategyType === 'keycloak') {
      const hasAccess = authentication?.data?.some((item) =>
        item?.details?.resource?.operations?.includes(uiResource)
      );
      return hasAccess;
    } else {
      // For Hybrid, check the resourceCode directly
      const targetResourceObject = authentication?.data?.find(
        (item) => item?.details?.resource?.resourceCode === uiResource
      );
      return !!targetResourceObject;
    }
  };

  useEffect(() => {
    fetchSingleScenario({
      scenarioID: match.params.id
    });
    configureRunInstanceAPIActions.fetchScenarioTypologies({
      id: match.params.id
    });
  }, [match.params.id]);

  useEffect(() => {
    const preventionAccess = hasAttributeValueByAttributeKey(
      authentication,
      'APP_3_RESOURCE_UI_RUN_INSTANCE',
      'PRODUCT_SOURCE_ID',
      TM_FRAUD_SOURCE_IDS.FRAUD_PREV_ID
    );
    if (!preventionAccess) {
      configureRunInstanceAPIActions.getLiveSets();
    }
    setPreventionAccess(preventionAccess);
  }, [authentication]);

  useEffect(() => {
    if (!isEmpty(error) && !isLoading) {
      message.error(SCENARIO_FAILED, 1.5, () => {
        returnToRunInstance();
      });
    }
  }, [error]);

  useEffect(() => () => triggerScenariosInit(), []);

  const dateSetOnChange = (value) => {
    setRunDataSet({
      ...runDataSet,
      ...value
    });
  };

  const checkEditedValue = (value) => {
    setIsValueEdited(value);
  };

  const onTabChange = (selectedTabKey) => {
    if (selectedTabKey !== '2') {
      if (isValueEdited) {
        Modal.confirm({
          ...CONFIRM_PROPS,
          onOk: () => {
            setIsValueEdited(false);
            setResetRI(true);
            setTabActiveKey(selectedTabKey);
          },
          onCancel: () => {
            setTabActiveKey('2');
          }
        });
        return;
      }
    }
    if (resetRI) {
      setResetRI(false);
    }
    setTabActiveKey(selectedTabKey);
  };

  const backToHome = (evt) => {
    evt.preventDefault();
    if (isValueEdited) {
      Modal.confirm({
        ...CONFIRM_PROPS,
        onOk: () => {
          setIsValueEdited(false);
          returnToRunInstance();
        }
      });
      return;
    }
    returnToRunInstance();
  };

  const validateTypologyBeforeSave = () => {
    if (typologiesData.filter((f) => f.useDefault).length === 0) {
      notification.open({
        message: (
          <p>
            <i style={{ color: '#e74c3c' }} className="fa fa-close"></i>
            &nbsp;&nbsp; {'Error!!'}
          </p>
        ),
        description: 'Please select at least one typology for default segment!'
      });
      return false;
    }

    const selectedCombination =
      createRunInstanceLiveSets?.data?.combinations?.filter((combinations) =>
        typologiesData.some((typology) =>
          typology.combinations.map((t) => t.id).includes(combinations.id)
        )
      );

    if (
      createRunInstanceLiveSets?.data?.combinations &&
      selectedCombination.length !==
        createRunInstanceLiveSets?.data?.combinations.length
    ) {
      notification.open({
        message: (
          <p>
            <i style={{ color: '#e74c3c' }} className="fa fa-close"></i>
            &nbsp;&nbsp; {'Error!!'}
          </p>
        ),
        description: 'Please select at least one typology for segment sets!'
      });
      return false;
    }

    return true;
  };

  const onLaunch = () => {
    const { trainingDates, predictionDates } = runDataSet;
    const [fromTrainingDate, toTrainingDate] = trainingDates?.item;
    const [fromPredictionDate, toPredictionDate] = predictionDates?.item;

    if (
      !preventionAccess &&
      !scenario?.latestInstance &&
      !validateTypologyBeforeSave()
    ) {
      return;
    }

    triggerScenarios({
      scenarioID: scenario.id,
      prediction: {
        dateRange: {
          from: convertLocalTimeToEpoch(
            fromPredictionDate.format(dateTimeFormat)
          ),
          to: convertLocalTimeToEpoch(toPredictionDate.format(dateTimeFormat))
        }
      },
      training: {
        dateRange: {
          from: convertLocalTimeToEpoch(
            fromTrainingDate.format(dateTimeFormat)
          ),
          to: convertLocalTimeToEpoch(toTrainingDate.format(dateTimeFormat))
        }
      }
    });
    returnToRunInstance();
  };

  return (
    <Layout className={style.runInstanceConfiglayout}>
      <PageHeader
        ghost={false}
        title={
          <Breadcrumb separator=">">
            <Breadcrumb.Item href="/app/trm/run-instance" onClick={backToHome}>
              Run Instance Name
            </Breadcrumb.Item>
            <Breadcrumb.Item>Configure Scenario</Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={[
          <Button
            type="primary"
            onClick={onLaunch}
            key="onLaunch"
            style={
              !(runDataSet?.trainingDates && runDataSet.predictionDates)
                ? {}
                : { backgroundColor: '#0c8ed7' }
            }
            disabled={
              !(runDataSet?.trainingDates && runDataSet.predictionDates)
            }>
            Launch
          </Button>
        ]}></PageHeader>
      <Spin spinning={isLoading}>
        <Tabs
          className={style.configNav}
          activeKey={tabActiveKey}
          destroyInactiveTabPane
          onChange={onTabChange}
          style={{ minHeight: 550 }}>
          <TabPane tab="Typologies" key="1">
            {scenario && (
              <Typologies
                scenarioName={scenario.name}
                scenarioId={scenarioID}
                executed={!!scenario.latestInstance}
                scenarioSource={scenario.sourceId}
                preventionAccess={preventionAccess}
                typologiesList={typologiesData}
                configureRunInstanceActions={configureRunInstanceAPIActions}
                liveSets={createRunInstanceLiveSets}
                dynpropsTabAccess={checkTabAccess(TM_UI_TYPOLOGY_DYNPROPS)}
              />
            )}
          </TabPane>
          {!preventionAccess && (
            <TabPane tab="Risk Indicators" key="2">
              {scenario && (
                <RiskIndicators
                  scenarioId={scenario.id}
                  checkEditedValue={checkEditedValue}
                  onCancel={returnToRunInstance}
                  reset={resetRI}
                  scenarioSource={scenario.sourceId}
                  scenarioTypologies={trmDashboard?.scenarioTypologies}
                />
              )}
            </TabPane>
          )}
          <TabPane tab="Dataset" key="3">
            {scenario && (
              <DataSet
                onChange={dateSetOnChange}
                trainingDays={runDataSet?.trainingDates?.days}
                predictionDays={runDataSet?.predictionDates?.days}
                scenarioSource={scenario.sourceId}
              />
            )}
          </TabPane>
          {checkTabAccess(TM_UI_RI_DYNPROPS) && (
            <TabPane tab="Dynamic Properties" key="4" destroyInactiveTabPane>
              {scenario && tabActiveKey === '4' && (
                <DynamicProperties
                  subModule="run_instance"
                  moduleId="3"
                  runInstanceId={scenarioID}
                />
              )}
            </TabPane>
          )}
        </Tabs>
      </Spin>
    </Layout>
  );
};

ConfigureScenario.propTypes = {
  scenario: PropTypes.object,
  scenarioAPIActions: PropTypes.object,
  isLoading: PropTypes.bool,
  match: PropTypes.object,
  error: PropTypes.object,
  history: PropTypes.object,
  trmDashboard: PropTypes.object,
  createRunInstanceLiveSets: PropTypes.object,
  configureRunInstanceAPIActions: PropTypes.object,
  authentication: PropTypes.object,
  createRunInstanceCategories: PropTypes.object,
  typologySegment: PropTypes.object
};

export default ConfigureScenario;
